import React, { useState, useEffect, useCallback } from 'react';
import { useMfgApi, errMsg } from './mfgApi';
import { useToast } from '../../hooks/useToast';
import { Plus, Loader2, Pencil, X, Trash2 } from 'lucide-react';
import { useMfgRole, MfgReadOnlyBadge } from './useMfgRole';

/* ── Generic CRUD Master Page ── */
export function MfgUnits() { return <MasterPage title="Production Units" endpoint="/units" fields={[{ key:'code', label:'Code', required:true }, { key:'name', label:'Name', required:true }]} hasActive testId="mfg-units" />; }
export function MfgSkus() { return <MasterPage title="SKUs" endpoint="/skus" fields={[{ key:'code', label:'Code', required:true }, { key:'name', label:'Name', required:true }, { key:'uom', label:'UOM', placeholder:'e.g. pieces' }, { key:'carton_pack_size_default', label:'Default Pack Size', type:'number' }]} hasActive testId="mfg-skus" />; }
export function MfgDestinations() { return <MasterPage title="Destinations" endpoint="/destinations" fields={[{ key:'name', label:'Name', required:true }, { key:'city', label:'City' }, { key:'address', label:'Address' }]} testId="mfg-destinations" />; }

function MasterPage({ title, endpoint, fields, hasActive, testId }) {
  const mfg = useMfgApi();
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | row
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { isOwner } = useMfgRole();
  const colCount = fields.length + (hasActive ? 1 : 0) + (isOwner ? 1 : 0);

  const load = useCallback(async () => { setLoading(true); try { const r = await mfg.get(endpoint); setRows(r.items || r || []); } catch {} finally { setLoading(false); } }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { const init = {}; fields.forEach(f => init[f.key] = ''); if (hasActive) init.is_active = true; setForm(init); setEditing('new'); setError(''); };
  const openEdit = (row) => { const init = {}; fields.forEach(f => init[f.key] = row[f.key] ?? ''); if (hasActive) init.is_active = row.is_active !== false; setForm(init); setEditing(row); setError(''); };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const body = { ...form };
      fields.forEach(f => { if (f.type === 'number' && body[f.key] !== '') body[f.key] = Number(body[f.key]); });
      if (editing === 'new') await mfg.post(endpoint, body);
      else await mfg.patch(`${endpoint}/${editing.id}`, body);
      toast.show(editing === 'new' ? 'Created' : 'Updated', 'success');
      setEditing(null); load();
    } catch (e) { setError(errMsg(e)); } finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name || row.code}"?`)) return;
    try { await mfg.del(`${endpoint}/${row.id}`); toast.show('Deleted', 'success'); load(); } catch (e) { toast.show(errMsg(e), 'error'); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid={`${testId}-page`}>
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">{title}<MfgReadOnlyBadge /></h1>
        {isOwner && <button data-testid={`${testId}-add`} onClick={openNew} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-500 text-white text-sm font-bold hover:bg-indigo-600 transition-colors"><Plus size={14} /> Add</button>}
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div> : (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm" data-testid={`${testId}-table`}>
            <thead><tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-100">
              {fields.map(f => <th key={f.key} className="text-left py-2.5 px-3">{f.label}</th>)}
              {hasActive && <th className="text-center py-2.5 px-3">Active</th>}
              {isOwner && <th className="py-2.5 px-3 w-20"></th>}
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={colCount} className="text-center py-12 text-gray-400">No records.</td></tr> : rows.map(r => (
                <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`${testId}-row-${r.id}`}>
                  {fields.map(f => <td key={f.key} className="py-2.5 px-3 text-gray-700">{r[f.key] ?? '—'}</td>)}
                  {hasActive && <td className="py-2.5 px-3 text-center">{r.is_active !== false ? '✅' : '❌'}</td>}
                  {isOwner && <td className="py-2.5 px-3 flex gap-1">
                    <button onClick={() => openEdit(r)} aria-label="Edit" className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-indigo-500" data-testid={`${testId}-edit-${r.id}`}><Pencil size={14} /></button>
                    <button onClick={() => remove(r)} aria-label="Delete" className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-red-500" data-testid={`${testId}-del-${r.id}`}><Trash2 size={14} /></button>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing !== null && isOwner && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid={`${testId}-modal`}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-extrabold text-gray-900">{editing === 'new' ? `Add ${title.replace(/s$/, '')}` : 'Edit'}</h3>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              {fields.map(f => (
                <label key={f.key} className="block">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{f.label}{f.required && ' *'}</span>
                  <input data-testid={`${testId}-field-${f.key}`} type={f.type || 'text'} className="input-sm mt-1" placeholder={f.placeholder || f.label} value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} />
                </label>
              ))}
              {hasActive && <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.is_active !== false} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} className="w-4 h-4 accent-indigo-500" /><span className="text-xs font-semibold text-gray-600">Active</span></label>}
              {error && <p className="text-red-500 text-xs font-medium">{error}</p>}
            </div>
            <button data-testid={`${testId}-save`} onClick={save} disabled={saving} className="w-full mt-4 h-11 rounded-xl bg-indigo-500 text-white font-bold text-sm hover:bg-indigo-600 disabled:opacity-50 transition-colors">
              {saving ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
